/**
 * Spec 0050 (U2) — per-product resource-limit **definitions** (levers) admin
 * section (saas-auth SASATH-0062). SUPER_ADMIN-only, mounted on the product
 * detail screen (the caller in ProductDetail gates rendering on
 * `operator?.isSuperAdmin`; this component additionally degrades to a contained
 * "SUPER_ADMIN required" state if the API returns 403). Self-fetches the
 * definition list on mount with isolated loading/empty/error states — it is NOT
 * folded into the shared productDetailLoader, so an ordinary SAAS_ADMIN viewer's
 * product page is never broken by a 403 here.
 *
 * A definition is a lever: `key` (immutable identity, set at create), `label`,
 * `valueType` ∈ {INT, BOOL, STRING} (fixed at create), and a nullable
 * `defaultValue` stored as a STRING interpreted per `valueType`. Create takes
 * key/label/valueType/default; edit touches **label + default only** (`key` and
 * `valueType` are immutable). The default is validated client-side to PARSE for
 * the chosen type (INT → integer, BOOL → true/false, STRING → any; blank → null).
 *
 * DELETE reflects the backend's in-use convention WITHOUT assuming one: on
 * success the row is dropped (a cascade or a clean delete both surface as 2xx);
 * on failure the backend's own message/code is surfaced via the snackbar and the
 * row stays (a block convention surfaces as its rejection). The UI bakes in
 * neither cascade nor block — it reflects whatever the API returns.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useEffect, useState } from 'react';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type { LimitDefinition, LimitValueType } from '@/api/types';
import { LIMIT_VALUE_TYPES } from '@/api/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';

function isForbidden(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403;
}

/**
 * Validate that a raw default string PARSES for the value type. Returns the
 * i18n suffix of the validation error, or null when valid. A blank default is
 * always valid (it means "no default" → null on the wire). STRING accepts any
 * non-blank text.
 */
function defaultValidationKey(valueType: LimitValueType, raw: string): string | null {
  const v = raw.trim();
  if (v === '') return null;
  if (valueType === 'INT') return /^-?\d+$/.test(v) ? null : 'badInt';
  if (valueType === 'BOOL') return v === 'true' || v === 'false' ? null : 'badBool';
  return null;
}

export function ProductLimitDefinitions({ productId }: { productId: number }) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const [rows, setRows] = useState<LimitDefinition[] | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDef, setEditDef] = useState<LimitDefinition | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<LimitDefinition | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setLoadError(null);
    try {
      const list = await apiAdmin.resourceLimits.definitions.list(productId);
      setRows(list);
      setForbidden(false);
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        setRows([]);
        return;
      }
      if (err instanceof ApiError) setLoadError(err);
      setRows([]);
    }
  }

  useEffect(() => {
    // `refresh()` only setStates after an `await`; wrap it in an inline async
    // IIFE so the effect body holds no synchronous setState (behavior identical).
    void (async () => {
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  function openAdd() {
    setEditDef(null);
    setDialogOpen(true);
  }

  function openEdit(def: LimitDefinition) {
    setEditDef(def);
    setDialogOpen(true);
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      await apiAdmin.resourceLimits.definitions.remove(productId, confirmDelete.id);
      snack.success(t('limitDefinitions.deleted', 'Limit definition deleted.'));
      setConfirmDelete(null);
      await refresh();
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        setConfirmDelete(null);
        return;
      }
      // Reflect the backend's in-use convention WITHOUT assuming one: surface the
      // backend's own message (e.g. a "definition in use" block) and keep the row.
      if (err instanceof ApiError) {
        snack.error(err.details.message ?? t(`errors.codes.${err.code}`, err.code));
      }
      setConfirmDelete(null);
    } finally {
      setBusy(false);
    }
  }

  if (forbidden) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h3" gutterBottom>
          {t('limitDefinitions.title', 'Resource-limit definitions')}
        </Typography>
        <Alert severity="warning">
          {t(
            'limitDefinitions.superAdminRequired',
            'Super admin required to manage resource-limit definitions.',
          )}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="h3">
          {t('limitDefinitions.title', 'Resource-limit definitions')}
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={openAdd}>
          {t('limitDefinitions.add', 'Add definition')}
        </Button>
      </Stack>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 2,
        }}
      >
        {t(
          'limitDefinitions.explanation',
          'Each definition is a per-product limit lever (e.g. number of locations). Plans set a value per lever; a tenant without a plan falls back to the default here. The key is fixed once created.',
        )}
      </Typography>
      <ErrorBanner error={loadError} />
      {rows === null ? (
        <Typography
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('common.loading', 'Loading')}
        </Typography>
      ) : rows.length === 0 ? (
        <Typography
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('limitDefinitions.empty', 'No limit definitions for this product yet.')}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('limitDefinitions.columns.key', 'Key')}</TableCell>
                <TableCell>{t('limitDefinitions.columns.label', 'Label')}</TableCell>
                <TableCell>
                  {t('limitDefinitions.columns.valueType', 'Value type')}
                </TableCell>
                <TableCell>
                  {t('limitDefinitions.columns.defaultValue', 'Default')}
                </TableCell>
                <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((def) => (
                <TableRow key={def.id}>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                      }}
                    >
                      {def.key}
                    </Typography>
                  </TableCell>
                  <TableCell>{def.label}</TableCell>
                  <TableCell>
                    <Chip
                      label={t(`limitDefinitions.valueTypes.${def.valueType}`, def.valueType)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {def.defaultValue == null ? (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {t('limitDefinitions.noDefault', '—')}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                        }}
                      >
                        {def.defaultValue}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label={t('common.edit', 'Edit')}
                      onClick={() => openEdit(def)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      aria-label={t('common.delete', 'Delete')}
                      onClick={() => setConfirmDelete(def)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <DefinitionDialog
        open={dialogOpen}
        productId={productId}
        definition={editDef}
        onClose={() => setDialogOpen(false)}
        onForbidden={() => {
          setDialogOpen(false);
          setForbidden(true);
        }}
        onSaved={() => {
          setDialogOpen(false);
          void refresh();
        }}
      />
      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title={t('limitDefinitions.deleteTitle', 'Delete limit definition?')}
        body={t(
          'limitDefinitions.deleteBody',
          'Delete {{key}}? Any plan values set for this lever are affected per the backend. This cannot be undone.',
          { key: confirmDelete?.key ?? '' },
        )}
        severity="error"
        confirmLabel={t('common.delete', 'Delete')}
        busy={busy}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
    </Paper>
  );
}

interface DefinitionDialogProps {
  open: boolean;
  productId: number;
  definition: LimitDefinition | null;
  onClose: () => void;
  onForbidden: () => void;
  onSaved: () => void;
}

function DefinitionDialog({
  open,
  productId,
  definition,
  onClose,
  onForbidden,
  onSaved,
}: DefinitionDialogProps) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const isEdit = definition !== null;
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  // `valueType` is fixed at create — the edit form renders it read-only.
  const [valueType, setValueType] = useState<LimitValueType>('INT');
  const [defaultValue, setDefaultValue] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [defaultError, setDefaultError] = useState<string | null>(null);
  const [prevOpen, setPrevOpen] = useState(open);
  const [prevDef, setPrevDef] = useState(definition);
  const [pending, setPending] = useState(false);

  // Seed the form from the opened definition via a render-phase state adjustment
  // (React's "adjust state while rendering" pattern) instead of a synchronous
  // setState in an effect — re-applies on the same open/definition changes.
  if (open !== prevOpen || definition !== prevDef) {
    setPrevOpen(open);
    setPrevDef(definition);
    if (open) {
      setKey(definition?.key ?? '');
      setLabel(definition?.label ?? '');
      setValueType(definition?.valueType ?? 'INT');
      setDefaultValue(definition?.defaultValue ?? '');
      setError(null);
      setDefaultError(null);
    }
  }

  async function onSubmit() {
    setError(null);
    // Validate the default PARSES for the chosen type before any network call.
    const invalid = defaultValidationKey(valueType, defaultValue);
    if (invalid) {
      setDefaultError(invalid);
      return;
    }
    setDefaultError(null);
    setPending(true);
    // Blank default → null ("no default"); otherwise the typed string verbatim.
    const trimmedDefault = defaultValue.trim();
    const wireDefault = trimmedDefault === '' ? null : trimmedDefault;
    try {
      if (isEdit && definition) {
        // Edit touches label + default ONLY — `key`/`valueType` are immutable.
        await apiAdmin.resourceLimits.definitions.update(productId, definition.id, {
          label: label.trim(),
          defaultValue: wireDefault,
        });
        snack.success(t('limitDefinitions.updated', 'Limit definition updated.'));
      } else {
        await apiAdmin.resourceLimits.definitions.create(productId, {
          key: key.trim(),
          label: label.trim(),
          valueType,
          defaultValue: wireDefault,
        });
        snack.success(t('limitDefinitions.created', 'Limit definition created.'));
      }
      onSaved();
    } catch (err) {
      if (isForbidden(err)) {
        onForbidden();
        return;
      }
      if (err instanceof ApiError) setError(err);
    } finally {
      setPending(false);
    }
  }

  const defaultHelper =
    defaultError !== null
      ? t(`limitDefinitions.validation.${defaultError}`, 'Invalid default value')
      : t(
          `limitDefinitions.fields.defaultHelper.${valueType}`,
          'The fallback value when no plan sets this lever. Leave blank for no default.',
        );

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit
          ? t('limitDefinitions.edit', 'Edit limit definition')
          : t('limitDefinitions.add', 'Add definition')}
      </DialogTitle>
      <DialogContent>
        <ErrorBanner error={error} />
        <Box
          component="form"
          id="limit-definition-form"
          onSubmit={(e) => {
            e.preventDefault();
            void onSubmit();
          }}
          noValidate
        >
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              required
              label={t('limitDefinitions.fields.key', 'Key')}
              value={key}
              onChange={(e) => setKey(e.target.value)}
              disabled={isEdit}
              helperText={t(
                'limitDefinitions.fields.keyHelper',
                "The stable lever identifier the product backend reads. Can't be changed after create.",
              )}
            />
            <TextField
              fullWidth
              required
              label={t('limitDefinitions.fields.label', 'Label')}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            <TextField
              select
              fullWidth
              label={t('limitDefinitions.fields.valueType', 'Value type')}
              value={valueType}
              onChange={(e) => {
                setValueType(e.target.value as LimitValueType);
                // A type change can invalidate the current default — re-check on submit.
                setDefaultError(null);
              }}
              disabled={isEdit}
              helperText={
                isEdit
                  ? t(
                      'limitDefinitions.fields.valueTypeImmutable',
                      "Can't be changed after create.",
                    )
                  : t(
                      'limitDefinitions.fields.valueTypeHelper',
                      'INT (integer), BOOL (true/false), or STRING (free text).',
                    )
              }
            >
              {LIMIT_VALUE_TYPES.map((vt) => (
                <MenuItem key={vt} value={vt}>
                  {t(`limitDefinitions.valueTypes.${vt}`, vt)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label={t('limitDefinitions.fields.defaultValue', 'Default value')}
              value={defaultValue}
              onChange={(e) => {
                setDefaultValue(e.target.value);
                if (defaultError) setDefaultError(null);
              }}
              error={defaultError !== null}
              helperText={defaultHelper}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          form="limit-definition-form"
          type="submit"
          variant="contained"
          disabled={pending}
        >
          {t('common.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
